// Autopilot — one control, in the page chrome.
//
// It used to be in two places at once. A cluster under the sidebar rail held
// three things (a radar linking to the dashboard, a Switch for the guided
// cursor, and a "?" explainer), and then the guide moved up into the header as
// its own icon — which consolidated nothing, it just split Autopilot across
// two corners of the screen.
//
// This is the single entry point. The icon is an icon: it opens a popover, it
// is not itself a switch. Everything that was in the rail cluster is in here,
// so nothing was dropped to achieve the consolidation:
//
//   • Open Autopilot   → the dashboard the radar used to link to
//   • Guide on/off     → the control the Switch used to be
//   • What is this?    → the copy the "?" used to hold
//
// The guide on/off could not simply move to the /autopilot page: that page
// carries no such control today, so linking there and deleting the switch
// would have made the guided cursor unreachable.
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { Radar, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { AUTOPILOT_ENABLED } from '@/lib/featureFlags';
import { useAutopilot } from '@/features/autopilot/AutopilotContext';
import { TOOLTIP_DELAY_MS } from './chromeControls';

export default function AutopilotMenu() {
  const { enabled, toggle } = useAutopilot();
  const [open, setOpen] = useState(false);
  const onToggle = useCallback(() => toggle(), [toggle]);

  // Same gate the rail cluster used — with the flag off there is no Autopilot,
  // so the chrome should not advertise it.
  if (!AUTOPILOT_ENABLED) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label="Autopilot"
                data-testid="autopilot-menu"
                className="relative flex items-center justify-center w-7 h-7 rounded-full bg-card shadow-sm hover:bg-muted/60 transition-colors"
              >
                <Radar
                  className={cn('w-3.5 h-3.5 transition-colors',
                    enabled ? 'text-accent' : 'text-gray-700')}
                  aria-hidden="true"
                />
                {/* A dot, not a filled icon: at this size a fill reads as a
                    different icon, whereas a dot reads as "the guide is on" —
                    the same grammar the notification badge uses beside it.
                    More true at 16px than it was at 20px. */}
                {enabled && (
                  <span
                    className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-accent"
                    aria-hidden="true"
                  />
                )}
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Autopilot</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* `align="start"`: the trigger moved to the LEFT of the header, so an
          end-aligned panel would open off toward the screen edge it is no
          longer near. */}
      <PopoverContent align="start" sideOffset={10} className="w-72 p-2">
        <Link to="/autopilot" onClick={() => setOpen(false)}>
          <div
            className="flex items-center justify-between rounded-lg px-3 py-2.5 hover:bg-muted cursor-pointer"
            data-testid="autopilot-open"
          >
            <span className="text-[13px] font-medium text-foreground">Open Autopilot</span>
            <ArrowRight className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
          </div>
        </Link>

        {/* The guide control. A row that toggles, rather than a Switch widget —
            the same reason the trigger above is an icon and not a switch. State
            is the word on the right, and `aria-pressed` for anyone not reading
            it visually. */}
        <button
          type="button"
          onClick={onToggle}
          aria-pressed={enabled}
          data-testid="autopilot-guide"
          className="w-full flex items-center justify-between rounded-lg px-3 py-2.5 hover:bg-muted"
        >
          <span className="text-[13px] font-medium text-foreground">Guided cursor</span>
          <span className={cn('text-[12px] font-semibold',
            enabled ? 'text-accent' : 'text-muted-foreground')}>
            {enabled ? 'On' : 'Off'}
          </span>
        </button>

        <div className="border-t border-border/60 mt-1 pt-2 px-3 pb-1">
          <p className="text-[12px] leading-relaxed text-muted-foreground">
            Autopilot watches your production plan, inventory and suppliers.
            When something threatens a run it surfaces the risk — what is at
            stake in dollars and the recommended fix — as a guided card on
            screen.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
